import { FC } from 'react';

import { CloudDownloadIcon, EmptyDocumentIcon } from '../../assets/icons/index';
import { useSelectedCompany } from '../../hooks/index';
import { downloadDocumentStorage } from '../../storage/modals/index';
import {
  downloadCompanyStorageFile,
  downloadCompanyStorageFilesSequentially,
} from '../../utils/company-storage-url.util';
import { getS3FileOriginalName } from '../../utils/get-s3file-original-name.util';
import { AlertDialogWrapper } from '../alert-dialog/index';
import { AppFormattedMessage } from '../app-formatted-message/index';
import Button from '../button/index';

export type FilesModalProps = {
  filesLinks: string[];
  onClose: () => void;
  onDownloadAll: () => void;
  onDownloadFile: (file: string) => void;
};

const Modal: FC<FilesModalProps> = ({ onClose, onDownloadAll, onDownloadFile, filesLinks }) => {
  return (
    <div className="m-2 flex max-w-[358px] flex-1 flex-col gap-8 rounded bg-white p-4 xs:max-w-[453px]">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 border-b-[1px] border-gray-100 pb-4">
          <span className="max-w-[358px] truncate text-xl font-semibold text-gray-700">
            {<AppFormattedMessage id="FILES" />}
          </span>
        </div>
        <div className="flex flex-col gap-4">
          <span className="text-xs font-[450] text-gray-500">
            <AppFormattedMessage id="ATTACHED_FILES" values={{ count: filesLinks.length }} />
          </span>
          <div className="flex flex-col gap-2">
            {filesLinks.map((file) => (
              <div
                className="flex w-full items-center justify-between gap-2 rounded border-[1px] border-brand-50 bg-brand-25 p-3"
                key={file}
              >
                <div className="flex items-center gap-2 overflow-hidden">
                  <EmptyDocumentIcon className="size-4" iconColor="#2565C8" />
                  <span className="truncate text-xs font-[450] text-brand-700">
                    {getS3FileOriginalName(file)}
                  </span>
                </div>
                <CloudDownloadIcon
                  className="size-4 cursor-pointer"
                  onClick={() => onDownloadFile(file)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex h-11 w-full justify-end gap-3">
        <Button
          className="h-full w-fit rounded border border-gray-300 px-6 py-[10px] text-sm font-[450] text-gray-700"
          onClick={onClose}
          styleType="NONE"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <Button
          className="h-full w-fit bg-brand-700 px-6 py-[10px] text-sm font-[550] text-white"
          onClick={onDownloadAll}
          styleType="NONE"
        >
          <AppFormattedMessage id="DOWNLOAD_ALL" />
        </Button>
      </div>
    </div>
  );
};

export const DownloadDocumentsModal: FC = () => {
  const { selectedCompany } = useSelectedCompany();
  const companyId = selectedCompany?.id;
  const fileLinks = downloadDocumentStorage(({ fileLinks }) => fileLinks);
  const close = downloadDocumentStorage(({ close }) => close);
  const isOpen = downloadDocumentStorage(({ isOpen }) => isOpen);

  const handleDocumentClick = (link: string) => {
    if (!companyId) return;
    void downloadCompanyStorageFile(companyId, link);
  };

  const handleDownloadAllDocuments = () => {
    if (!companyId) return;
    void downloadCompanyStorageFilesSequentially(companyId, fileLinks);
  };

  if (isOpen && fileLinks.length === 1 && companyId) {
    void downloadCompanyStorageFile(companyId, fileLinks[0]);
    close();
    return;
  }

  return (
    <AlertDialogWrapper control={{ open: isOpen }}>
      <Modal
        filesLinks={fileLinks}
        onClose={close}
        onDownloadAll={handleDownloadAllDocuments}
        onDownloadFile={handleDocumentClick}
      />
    </AlertDialogWrapper>
  );
};
